/**
 * The Explorer landing in Cosmos (onboarding v2, spec 13 Oct 2026): in place
 * of "Popular technologies", a heading, the 3 technologies picked from the
 * onboarding choice, then the upgrade block — and nothing below it.
 *
 *   industry + keyword  "PICKED FOR YOU" · "Top 3 for <Industry> · <Keyword>"
 *   industry only       "PICKED FOR YOU" · "Top 3 for <Industry>"
 *   skipped / random    "POPULAR ON COSMOS" (no heading by default)
 *
 * The copy is admin-editable (Admin → Onboarding → Settings) and arrives with
 * the public onboarding config; DEFAULT_ONBOARDING_COPY covers a server that
 * sends none. The cards are the usual tech cards (accordion or legacy, per
 * the admin's design switch) with surface 'cosmos_picks' and rank 1-3, so
 * every techcard_* event inside a pick is attributable. Opening a pick also
 * fires cosmos_picks_card_open for both designs (the legacy list has no open
 * event of its own).
 *
 * DirectSearchPage decides WHEN this shows (an Explorer with the lock on and
 * no search on screen) and loads the picks; this only renders them.
 */
import React from 'react';
import { trackClick } from '../../utils/analytics';
import { CardContactTarget, TechAccordionCard } from '../techcard/TechAccordionCard';
import type { CardPreview, TechCardDesign } from '../techcard/api';
import { fillPicksHeading, OnboardingCopy, resolveOnboardingCopy } from '../../types/onboarding';
import { PicksTrialInfo, PicksUpgradeBlock } from './PicksUpgradeBlock';

export type PicksViewMode = 'industry_keyword' | 'industry' | 'random';

/** One pick as the list already has it (no card payload needed to render). */
export interface PickCard {
  techId: string;
  preview: CardPreview;
  /** The card's university id: its university link is the (locked) filter. */
  universityId?: string;
}

export interface ExplorerPicksProps {
  cards: PickCard[];
  /** A load is in flight: placeholders while there is nothing to show yet. */
  loading: boolean;
  mode: PicksViewMode;
  industry: string | null;
  keyword: string | null;
  copy?: Partial<OnboardingCopy> | null;
  design: TechCardDesign;
  /** Accordion: the one open card (the page's own state, keyed `${id}#${i}`). */
  openCardId: string | null;
  onToggleCard: (key: string | null) => void;
  savedIds: Set<string>;
  onBookmarkChange: (ids: string[], on: boolean) => void;
  onContact?: (card: CardContactTarget) => void;
  /** Legacy design: opens the technology window. Accordion: Similar tech. */
  onReadMore?: (id: string) => void;
  onUniversityClick?: (code?: string) => void;
  /** For the block's "all {tech_count} tech offers". */
  techCount: number | null;
  trial?: PicksTrialInfo | null;
  onStartTrial?: (origin: string) => void;
}

/** Three grey placeholders, the height of collapsed cards. */
export const PicksSkeleton: React.FC<{ withHeading?: boolean }> = ({ withHeading }) => (
  <div aria-busy="true" aria-live="polite" data-testid="picks-skeleton">
    <span className="sr-only">Loading technologies…</span>
    {withHeading && (
      <div className="mb-3 space-y-1.5" aria-hidden="true">
        <div className="h-2.5 w-24 rounded bg-grayscale-200 animate-pulse" />
        <div className="h-4 w-64 max-w-full rounded bg-grayscale-200 animate-pulse" />
      </div>
    )}
    <ul className="space-y-3" aria-hidden="true">
      {[0, 1, 2].map(i => (
        <li key={i} className="rounded-xl border border-grayscale-200 p-4 animate-pulse">
          <div className="h-4 w-3/4 rounded bg-grayscale-200" />
          <div className="h-3 w-1/3 rounded bg-grayscale-200 mt-2" />
          <div className="h-3 w-full rounded bg-grayscale-200 mt-3" />
        </li>
      ))}
    </ul>
  </div>
);

export const ExplorerPicks: React.FC<ExplorerPicksProps> = ({
  cards, loading, mode, industry, keyword, copy, design, openCardId, onToggleCard, savedIds, onBookmarkChange,
  onContact, onReadMore, onUniversityClick, techCount, trial, onStartTrial,
}) => {
  const c = resolveOnboardingCopy(copy);
  const random = mode === 'random' || !industry;
  const eyebrow = random ? c.picks_eyebrow_random : c.picks_eyebrow;
  const heading = random
    ? fillPicksHeading(c.picks_heading_random)
    : mode === 'industry_keyword' && keyword
      ? fillPicksHeading(c.picks_heading_kw, industry, keyword)
      : fillPicksHeading(c.picks_heading_industry, industry);

  const opened = (techId: string, position: number) =>
    trackClick('cosmos_picks_card_open', { tech_id: techId, position, mode, design });

  return (
    <section aria-label={heading || eyebrow || 'Technologies'} data-testid="explorer-picks">
      <div className="mb-3">
        {eyebrow && (
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-600">{eyebrow}</div>
        )}
        {heading && (
          <h2 className="text-base sm:text-lg font-semibold text-grayscale-1300 leading-snug mt-0.5">{heading}</h2>
        )}
      </div>

      {cards.length === 0 && loading && <PicksSkeleton />}
      {cards.length === 0 && !loading && (
        <div className="text-sm text-grayscale-1100">
          We couldn&apos;t load technologies just now. Please try again in a moment.
        </div>
      )}

      {cards.length > 0 && (design === 'accordion' ? (
        <ul className="space-y-3">
          {cards.map((p, i) => {
            const cardKey = `${p.techId}#${i}`;
            return (
              <li key={`pick-${i}-${p.techId}`}>
                <TechAccordionCard
                  techId={p.techId}
                  preview={p.preview}
                  expanded={openCardId === cardKey}
                  onToggle={open => {
                    if (open) opened(p.techId, i + 1);
                    onToggleCard(open ? cardKey : null);
                  }}
                  rank={i + 1}
                  surface="cosmos_picks"
                  bookmarked={savedIds.has(p.techId)}
                  onBookmarkChange={(id, on) => onBookmarkChange([id, p.techId], on)}
                  onContact={onContact}
                  onOpenTech={id => onReadMore && onReadMore(id)}
                  onUniversityClick={u => onUniversityClick?.(u.code || p.universityId)}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="space-y-3">
          {cards.map((p, i) => {
            const { title, university, country, trl, summary } = p.preview;
            return (
              <li
                key={`pick-${i}-${p.techId}`}
                className="border border-grayscale-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer utech-nocopy"
                onClick={() => {
                  opened(p.techId, i + 1);
                  if (onReadMore) onReadMore(p.techId);
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-semibold text-grayscale-1300">{title || 'Untitled'}</h3>
                  {trl !== null && trl !== undefined && trl !== '' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">TRL {trl}</span>
                  )}
                </div>
                <div className="text-xs text-grayscale-1100 mt-1">
                  {[university, country].filter(Boolean).join(' · ')}
                </div>
                {summary && (
                  <p className="text-sm text-grayscale-1200 mt-2 line-clamp-2 overflow-hidden">{summary}</p>
                )}
              </li>
            );
          })}
        </ul>
      ))}

      <PicksUpgradeBlock
        mode={mode}
        industry={industry}
        keyword={keyword}
        techCount={techCount}
        trial={trial}
        onStartTrial={onStartTrial}
      />
    </section>
  );
};

export default ExplorerPicks;
